import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggerService } from '@klickdata/core/application';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MobileService } from '@klickdata/core/mobile';
import { Resource, ResourceSocketService } from '@klickdata/core/resource';
import { OccasionDate } from '@klickdata/core/resource/src/resource.model';
import { StartResourceService } from '@klickdata/core/resource/src/start-resource.service';
import { ResourceTypes } from '@klickdata/core/resource/src/types.enum';
import {
    Filter,
    GlobalFilterProperty,
    GlobalFilterPropertyType,
    SelectFilterOption,
    TableFilterComponent,
} from '@klickdata/core/table';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { ResourceTableBaseComponent } from '@klickdata/shared-components/src/resource-table-base/resource-table-base.component';
import { ShareService } from '@klickdata/shared-components/src/share/share.service';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { BehaviorSubject, filter, Observable, takeUntil } from 'rxjs';

@Component({
    selector: 'app-instructor-signoffs-listing',
    templateUrl: './instructor-signoffs-listing.component.html',
    styleUrls: ['./instructor-signoffs-listing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstructorSignoffsListingComponent extends OnDestroyHandler implements AfterViewInit, OnInit {
    @ViewChild('tableBase') tableBase: ResourceTableBaseComponent;
    @Input() tableFilterItems: Filter<any>[];
    @Input() user: User;
    @Output() onSelected: EventEmitter<number> = new EventEmitter<number>();
    @ViewChild('title', { static: true }) title: TemplateRef<any>;
    @ViewChild('img', { static: true }) img: TemplateRef<any>;
    @ViewChild('enrollment', { static: true }) enrollment: TemplateRef<any>;
    @ViewChild('signoffs', { static: true }) signoffs: TemplateRef<any>;
    @ViewChild('approved_users', { static: true }) approved_users: TemplateRef<any>;
    @ViewChild('pending_users', { static: true }) pending_users: TemplateRef<any>;
    @ViewChild('dismissed_users', { static: true }) dismissed_users: TemplateRef<any>;
    @ViewChild('type', { static: true }) type: TemplateRef<any>;
    @ViewChild('enrollment_date', { static: true }) enrollment_date: TemplateRef<any>;
    @ViewChild('expansionContextTemplate', { static: true }) expansionContextTemplate: TemplateRef<any>;
    public approvalStatusSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
    @ViewChild('tools', { static: true }) tools: TemplateRef<any>;
    public resourceTypeOptions: SelectFilterOption[];
    public shareLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    GlobalFilterPropertyType = GlobalFilterPropertyType;
    public isMobile$: Observable<boolean>;
    public customer$: Observable<Customer>;
    @Input() public expandedRowId: number | null;
    GlobalFilterProperty = GlobalFilterProperty;
    columnsData: { [key: string]: TemplateRef<any> } = {};
    otherContext: { [key: string]: TemplateRef<any> } = {};
    columnsKeys: any = {
        img: { label: $localize`Image`, sortable: false },
        title: { label: $localize`Title`, sortable: true },
        // enrollment: { label: $localize`Enrollment`, sortable: true },
        approved_users: { label: $localize`Approved`, sortable: true, icon: 'check_circle' },
        dismissed_users: { label: $localize`Dismissed`, sortable: true, icon: 'cancel' },
        pending_users: { label: $localize`Pending`, sortable: true, icon: 'pending_actions' },
        type: { label: $localize`Type`, sortable: false },
        // signoffs: { label: $localize`Signoffs`, sortable: true },
    };
    ResourceTypes = ResourceTypes;
    expandedFn = (resource: Resource) => (resource.opportunity_id || resource.id) === this.expandedRowId;

    constructor(
        protected downloadHelper: DownloadHelperService,
        public shareService: ShareService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected cdr: ChangeDetectorRef,
        protected mobile: MobileService,
        protected auth: AuthService,
        protected startResourceService: StartResourceService,
        protected resourceSocketService: ResourceSocketService,
        protected logger: LoggerService
    ) {
        super();

        this.resourceTypeOptions = [
            { title: $localize`Course`, value: 3, icon: 'kd-icon-task', isMainResource: true },
            { title: $localize`Event`, value: 25, icon: 'event', isMainResource: false },
        ];
        this.isMobile$ = mobile.isMobile();
    }
    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                filter((params) => params.expand),
                takeUntil(this.destroy)
            )
            .subscribe((params) => {
                setTimeout(() => {
                    this.expandedRowId = +params.expand;
                    this.cdr.markForCheck();
                }, 500);
            });
        this.customer$ = this.auth.getCustomer();
    }
    ngAfterViewInit(): void {
        this.columnsData['img'] = this.img;
        this.columnsData['title'] = this.title;
        this.columnsData['type'] = this.type;

        let actionSocket: Observable<Resource>;

        this.columnsData['signoffs'] = this.signoffs;
        this.columnsKeys['signoffs'] = { label: $localize`Signoffs`, icon: 'app_registration', sortable: true };
        actionSocket = this.resourceSocketService.listenToNewResource('SignedoffResourceEvent');

        actionSocket.pipe(takeUntil(this.destroy)).subscribe((resource) => {
            this.logger.log('actionSocket', resource);
            this.tableBase.dataSource.replace(resource);
        });

        this.columnsData['approved_users'] = this.approved_users;
        this.columnsData['dismissed_users'] = this.dismissed_users;
        this.columnsData['pending_users'] = this.pending_users;
        this.columnsData['tools'] = this.tools;
        this.otherContext['expansionContext'] = this.expansionContextTemplate;
    }
    public getPlayBtnTxt(resource: Resource): string {
        return resource.opportunity_id ? $localize`:@@continue:Continue` : $localize`:@@play:Play`;
    }
    retake(resource: Resource) {
        this.startResourceService.takeResource(resource, this.user)?.subscribe();
    }
    public download(resource: Resource) {
        this.downloadHelper.downloadWithOptions(resource.downloads);
    }
    browse(resource: Resource) {
        this.router.navigate(['/home/dashboard/resource-details/' + resource.id]);
    }
    public getOccasionDates(resource: Resource): OccasionDate[] {
        const dates = resource.user_collaboration?.enroll_approval_required?.metadata?.occasion_dates;
        return !!dates?.length ? Utils.filterEmptyOccasionDates(dates) : [];
    }
    canExpand(resource: Resource) {
        return (
            resource.opportunity_id ||
            ResourceTypes.parentType(resource.type_id) === ResourceTypes.COURSE ||
            ResourceTypes.parentType(resource.type_id) === ResourceTypes.EVENT
        );
    }

    public play(resource: Resource): void {
        this.startResourceService.takeResource(resource, this.user)?.subscribe();
    }

    onChangeStatus() {
        this.tableBase.dataSource.refresh();
    }
    edit(resource: Resource) {
        if (resource.type_id === ResourceTypes.GeneralCoursePlan) {
            this.user.isAdmin()
                ? this.router.navigate(['/admin/content/courses/', resource.id])
                : this.router.navigate(['/home/manage/course-plan/', resource.id]);
        } else {
            this.user.isAdmin()
                ? this.router.navigate([`/admin/content/${ResourceTypes.playerRoute(resource.type_id)}s/`, resource.id])
                : this.router.navigate([`/home/manage/${ResourceTypes.playerRoute(resource.type_id)}/`, resource.id]);
        }
    }
    expansionToggled(resource: Resource) {
        if (this.canExpand(resource)) {
            const expandId = resource.opportunity_id || resource.id;
            this.onSelected.emit(expandId);
            this.expandedRowId = this.expandedRowId === expandId ? null : expandId;
        }
    }
}
